import { motion } from "framer-motion"
import { FileSignature, Calendar, CheckCircle2, Clock, ExternalLink } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

const agreements = [
  {
    id: "agr-001",
    type: "SOW" as const,
    projectName: "E-Commerce Platform Rebuild",
    signDate: "2025-01-03",
    signOffDate: "2025-01-04",
    status: "active" as const,
    contractLink: "https://example.com/contracts/sow-001.pdf",
  },
  {
    id: "agr-002",
    type: "NDA" as const,
    projectName: "Mobile App Development",
    signDate: "2025-01-10",
    signOffDate: "2025-01-12",
    status: "active" as const,
    contractLink: "https://example.com/contracts/nda-002.pdf",
  },
  {
    id: "agr-003",
    type: "SOW" as const,
    projectName: "API Integration Project",
    signDate: "2024-10-15",
    signOffDate: "2024-12-20",
    status: "completed" as const,
    contractLink: "https://example.com/contracts/sow-003.pdf",
  },
]

export function AgreementsPage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">Agreements & Contracts</h1>
        <p className="text-muted-foreground">
          View and manage your signed agreements and project contracts.
        </p>
      </div>

      <div className="space-y-4">
        {agreements.map((agreement) => (
          <Card key={agreement.id} className="glass-card border-gradient">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <FileSignature className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{agreement.projectName}</h3>
                    <div className="flex items-center gap-3 mt-2">
                      <Badge variant="outline">{agreement.type}</Badge>
                      <Badge
                        className={
                          agreement.status === "active"
                            ? "bg-emerald-400/20 text-emerald-400"
                            : "bg-slate-400/20 text-slate-400"
                        }
                      >
                        {agreement.status === "active" ? (
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                        ) : (
                          <Clock className="h-3 w-3 mr-1" />
                        )}
                        {agreement.status}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Button variant="outline" size="sm" asChild>
                  <a href={agreement.contractLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Document
                  </a>
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mt-6 pt-4 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Signed</p>
                    <p className="text-sm font-medium">
                      {new Date(agreement.signDate).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Sign-off Date</p>
                    <p className="text-sm font-medium">
                      {new Date(agreement.signOffDate).toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </motion.div>
  )
}

